// batch-71 kennismaking (FO-534, spec 234) - types, DTO's en Zod-schemas van de
// kennismaking voor de start en de nalooplijst.
//
// De kennismaking is mijlpaal M2B (zie `contract-mijlpalen.ts`): op START -10
// werkdagen een gesprek, een taak met uitvoerder en tijd, een intern planbord-blok
// en een Outlook-uitnodiging. De lijst toont per onderwerp de stand uit de app (de
// checks lezen de bestaande bronnen, er is geen eigen statusbron) plus wat de
// gespreksvoerder zelf vastlegt: besproken, niet van toepassing, een notitie.
//
// Zichtbare teksten zonder em-dash. Geen BSN, salaris of andere gevoelige gegevens
// in DTO's: dit is de werkvoorraad van de gespreksvoerder, geen dossier.

import { z } from 'zod'
import { queryBoolean } from './zod-query'

// ── Vaste waarden ────────────────────────────────────────────────────────────

/** De mijlpaalcode van de kennismaking voor de start (M2 blijft de melding). */
export const KENNISMAKING_MIJLPAAL_CODE = 'M2B' as const
/** `GesprekTypeConfig.key` van het gesprek. */
export const KENNISMAKING_GESPREK_TYPE_KEY = 'KENNISMAKING_VOOR_START'
export const KENNISMAKING_GESPREK_LABEL = 'Kennismaking voor de start'
/** Standaardtijd van het gesprek (NL-wandklok). */
export const KENNISMAKING_STANDAARD_TIJD = '10:00'
export const KENNISMAKING_DUUR_MINUTEN = 45
export const KENNISMAKING_LOCATIE = 'Telefonisch / Teams'
/** Actor van alles wat de motor zelf doet (audit), geen user-id. */
export const KENNISMAKING_SYSTEEM_ACTOR = 'system:kennismaking'

// ── Groepen en checks ────────────────────────────────────────────────────────

export const KENNISMAKING_GROEPEN = [
  'MIDDELEN',
  'PROCEDURES',
  'VERLOF_OPBOUW',
  'CERTIFICATEN',
  'CURSUSSEN',
  'PRAKTISCH',
  'OVERIG',
] as const
export type KennismakingGroep = (typeof KENNISMAKING_GROEPEN)[number]

export const KENNISMAKING_GROEP_LABELS: Record<KennismakingGroep, string> = {
  MIDDELEN: 'Middelen',
  PROCEDURES: 'Procedures uitgelegd',
  VERLOF_OPBOUW: 'Verlof en opbouw',
  CERTIFICATEN: 'Certificaten',
  CURSUSSEN: 'Cursussen',
  PRAKTISCH: 'Praktisch',
  OVERIG: 'Overig',
}

/**
 * De check-registry (`apps/api/src/lib/kennismaking/checks.ts`). Elke sleutel leest
 * een bestaande bron; de drift-test houdt deze lijst en de registry gelijk.
 */
export const KENNISMAKING_CHECK_SLEUTELS = [
  'onboarding.middel',
  'onboarding.asset-reservering',
  'tankpas.houder',
  'employee.maten-bekend',
  'employee.account-aanwezig',
  'document.leesbevestiging',
  'contract.opbouwkeuze-gezet',
  'verlof.pro-rata-toekenning',
  'certificaat.verplicht-status',
  'opleiding.deelname-zonder-sessie',
  'contract.eerste-werkdag',
  'employee.rooster',
  'mijlpaal.buddy',
  'planbord.eerste-week',
] as const
export type KennismakingCheckSleutel = (typeof KENNISMAKING_CHECK_SLEUTELS)[number]

export const KENNISMAKING_CHECK_LABELS: Record<KennismakingCheckSleutel, string> = {
  'onboarding.middel': 'Middel uit de start-checklist van de onboarding',
  'onboarding.asset-reservering': 'Reservering op de onboarding',
  'tankpas.houder': 'Tankpas op naam',
  'employee.maten-bekend': 'Kleding en PBM: maten en bestelling',
  'employee.account-aanwezig': 'Account en toegang',
  'document.leesbevestiging': 'Reglementen gelezen',
  'contract.opbouwkeuze-gezet': 'Keuze ATV of TVT vastgelegd',
  'verlof.pro-rata-toekenning': 'Verlof toegekend voor het startjaar',
  'certificaat.verplicht-status': 'Verplichte certificaten',
  'opleiding.deelname-zonder-sessie': 'Nog te plannen opleidingen',
  'contract.eerste-werkdag': 'Eerste werkdag',
  'employee.rooster': 'Rooster en ploeg',
  'mijlpaal.buddy': 'Buddy (startchecklist)',
  'planbord.eerste-week': 'Planning eerste week',
}

/**
 * De automatische status van een onderwerp. GEREGELD = groen, BEZIG = oranje
 * (gereserveerd, besteld, loopt), OPEN = rood. ONBEKEND = de check kon het niet
 * vaststellen (nooit stil groen, FO-534 §6.7). GEEN_BRON = geen automatische status:
 * dit onderwerp wordt alleen besproken.
 */
export const KENNISMAKING_AUTO_STATUSSEN = ['GEREGELD', 'BEZIG', 'OPEN', 'ONBEKEND', 'GEEN_BRON'] as const
export type KennismakingAutoStatus = (typeof KENNISMAKING_AUTO_STATUSSEN)[number]

export const KENNISMAKING_AUTO_STATUS_LABELS: Record<KennismakingAutoStatus, string> = {
  GEREGELD: 'Geregeld',
  BEZIG: 'Loopt',
  OPEN: 'Open',
  ONBEKEND: 'Onbekend',
  GEEN_BRON: 'Bespreken',
}

export const KENNISMAKING_CHECKLIST_STATUSSEN = ['OPEN', 'AFGEROND', 'GEANNULEERD'] as const
export type KennismakingChecklistStatus = (typeof KENNISMAKING_CHECKLIST_STATUSSEN)[number]

export const KENNISMAKING_CHECKLIST_STATUS_LABELS: Record<KennismakingChecklistStatus, string> = {
  OPEN: 'Open',
  AFGEROND: 'Afgerond',
  GEANNULEERD: 'Geannuleerd',
}

export const KENNISMAKING_AUDIT_ACTIES = [
  'AANGEMAAKT',
  'BESPROKEN',
  'NIET_BESPROKEN',
  'NIET_VAN_TOEPASSING',
  'WEL_VAN_TOEPASSING',
  'NOTITIE',
  'ACTIEPUNT_GEMAAKT',
  'ACTIEPUNT_INGETROKKEN',
  'VERPLAATST',
  'VERSCHOVEN',
  'VERANTWOORDELIJKE_GEWIJZIGD',
  'AFGEZEGD',
  'AFGEROND',
  'AGENDA_MISLUKT',
  'HEROPEND',
] as const
export type KennismakingAuditActie = (typeof KENNISMAKING_AUDIT_ACTIES)[number]

/** Van waar een verplaatsing kwam (voor de audit en de melding). */
export const KENNISMAKING_VERPLAATS_BRONNEN = ['GESPREK', 'OUTLOOK', 'TAAK', 'LIJST', 'STARTDATUM', 'AGENDA'] as const
export type KennismakingVerplaatsBron = (typeof KENNISMAKING_VERPLAATS_BRONNEN)[number]

// ── Het standaardsjabloon (FO-534 §B6) ───────────────────────────────────────

export interface KennismakingSjabloonRegelBasis {
  code: string
  groep: KennismakingGroep
  label: string
  toelichting: string | null
  checkSleutel: KennismakingCheckSleutel | null
  checkParams: Record<string, string> | null
  vrijeTekst: boolean
  voorMedewerker: boolean
}

export const KENNISMAKING_STANDAARD_SJABLOON_NAAM = 'Standaard nalooplijst'

/**
 * De geseede lijst (FO-534 §B6). De ENIGE bron: de migratie is hiermee gevuld (de
 * drift-test vergelijkt beide), de motor valt erop terug als er geen sjabloon in de
 * database staat. HR beheert de rij in de database onder Instellingen > Onboarding.
 */
export const KENNISMAKING_STANDAARD_REGELS: readonly KennismakingSjabloonRegelBasis[] = [
  { code: 'AUTO', groep: 'MIDDELEN', label: 'Auto', toelichting: 'Voertuig gereserveerd of toegewezen. Geen auto bij deze functie? Zet hem op niet van toepassing.', checkSleutel: 'onboarding.middel', checkParams: { stap: 'VOERTUIG' }, vrijeTekst: false, voorMedewerker: false },
  { code: 'TANKPAS', groep: 'MIDDELEN', label: 'Tankpas', toelichting: null, checkSleutel: 'tankpas.houder', checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'GEREEDSCHAP', groep: 'MIDDELEN', label: 'Gereedschap', toelichting: 'Samenstel-opdracht of uitgifte van het gereedschapspakket.', checkSleutel: 'onboarding.middel', checkParams: { stap: 'GEREEDSCHAP' }, vrijeTekst: false, voorMedewerker: false },
  { code: 'KLEDING', groep: 'MIDDELEN', label: 'Kleding en PBM', toelichting: 'Maten bekend en de bestelling geplaatst.', checkSleutel: 'employee.maten-bekend', checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'TELEFOON', groep: 'MIDDELEN', label: 'Telefoon en SIM', toelichting: null, checkSleutel: 'onboarding.middel', checkParams: { stap: 'TELEFOON_SIM' }, vrijeTekst: false, voorMedewerker: false },
  { code: 'LAPTOP', groep: 'MIDDELEN', label: 'Laptop', toelichting: 'Alleen als de functie een laptop heeft.', checkSleutel: 'onboarding.asset-reservering', checkParams: { type: 'LAPTOP' }, vrijeTekst: false, voorMedewerker: false },
  { code: 'ACCOUNTS', groep: 'MIDDELEN', label: 'Accounts en toegang', toelichting: 'Werkaccount aangemaakt en toegang tot de portal.', checkSleutel: 'employee.account-aanwezig', checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'PROC_VERLOF', groep: 'PROCEDURES', label: 'Verlof aanvragen', toelichting: null, checkSleutel: null, checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'PROC_ZIEK', groep: 'PROCEDURES', label: 'Ziek- en betermelden', toelichting: null, checkSleutel: null, checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'PROC_UREN', groep: 'PROCEDURES', label: 'Uren en werkbonnen', toelichting: null, checkSleutel: null, checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'PROC_DECLARATIES', groep: 'PROCEDURES', label: 'Declaraties', toelichting: null, checkSleutel: null, checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'PROC_OPLEIDINGEN', groep: 'PROCEDURES', label: 'Opleidingen aanvragen', toelichting: null, checkSleutel: null, checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'PROC_VEILIGHEID', groep: 'PROCEDURES', label: 'Veiligheid en PBM', toelichting: null, checkSleutel: null, checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'REGLEMENTEN', groep: 'PROCEDURES', label: 'Bedrijfsregels en reglementen gelezen', toelichting: 'De leesbevestigingen op de reglementen van de onboarding.', checkSleutel: 'document.leesbevestiging', checkParams: null, vrijeTekst: false, voorMedewerker: true },
  { code: 'ATV_KEUZE', groep: 'VERLOF_OPBOUW', label: 'ATV-opbouw: keuze', toelichting: 'De keuze legt HR vast op het verlofprofiel.', checkSleutel: 'contract.opbouwkeuze-gezet', checkParams: { soort: 'ATV' }, vrijeTekst: false, voorMedewerker: true },
  { code: 'TVT_KEUZE', groep: 'VERLOF_OPBOUW', label: 'TVT werkuren en reisuren: keuze', toelichting: 'De keuze legt HR vast op het verlofprofiel.', checkSleutel: 'contract.opbouwkeuze-gezet', checkParams: { soort: 'TVT' }, vrijeTekst: false, voorMedewerker: true },
  { code: 'VERLOFSALDO', groep: 'VERLOF_OPBOUW', label: 'Verlofsaldo startjaar uitgelegd', toelichting: null, checkSleutel: 'verlof.pro-rata-toekenning', checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'CERTIFICATEN', groep: 'CERTIFICATEN', label: 'Verplichte certificaten', toelichting: 'Per certificaat: geupload, loopt of ontbreekt.', checkSleutel: 'certificaat.verplicht-status', checkParams: null, vrijeTekst: false, voorMedewerker: true },
  { code: 'CURSUSSEN', groep: 'CURSUSSEN', label: 'Nog te plannen opleidingen', toelichting: 'Deelnames zonder sessie en verplichte certificaten zonder deelname.', checkSleutel: 'opleiding.deelname-zonder-sessie', checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'EERSTE_WERKDAG', groep: 'PRAKTISCH', label: 'Eerste werkdag, tijd en locatie', toelichting: null, checkSleutel: 'contract.eerste-werkdag', checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'ROOSTER', groep: 'PRAKTISCH', label: 'Rooster en ploeg', toelichting: null, checkSleutel: 'employee.rooster', checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'BUDDY', groep: 'PRAKTISCH', label: 'Buddy', toelichting: 'Uit de checklist van de kennismaking-taak (M1).', checkSleutel: 'mijlpaal.buddy', checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'EERSTE_WEEK', groep: 'PRAKTISCH', label: 'Klant of project eerste week', toelichting: 'Wat er op het planbord staat voor de eerste werkweek.', checkSleutel: 'planbord.eerste-week', checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'REISTIJD', groep: 'PRAKTISCH', label: 'Reistijd en woon-werk', toelichting: null, checkSleutel: null, checkParams: null, vrijeTekst: false, voorMedewerker: false },
  { code: 'VRAGEN', groep: 'OVERIG', label: 'Vragen van de medewerker', toelichting: null, checkSleutel: null, checkParams: null, vrijeTekst: true, voorMedewerker: false },
  { code: 'BIJZONDERHEDEN', groep: 'OVERIG', label: 'Bijzonderheden', toelichting: null, checkSleutel: null, checkParams: null, vrijeTekst: true, voorMedewerker: false },
]

// ── DTO's ────────────────────────────────────────────────────────────────────

/** Een regel binnen een onderwerp, bijvoorbeeld een certificaat of een cursus. */
export interface KennismakingDetailDTO {
  label: string
  status: KennismakingAutoStatus
  statusLabel: string
  detail: string | null
  /** Knop bij de regel, bijvoorbeeld "plannen" bij een cursus zonder sessie. */
  actie: { label: string; href: string } | null
}

export interface KennismakingActiepuntDTO {
  /** GespreksverslagAfspraak.id */
  id: string
  regelId: string | null
  regelLabel: string | null
  omschrijving: string
  /** yyyy-mm-dd */
  deadline: string
  status: 'OPEN' | 'AFGEHANDELD'
  houderNamen: string[]
  gedeeldMetMedewerker: boolean
}

export interface KennismakingRegelDTO {
  id: string
  code: string
  groep: KennismakingGroep
  label: string
  toelichting: string | null
  vrijeTekst: boolean
  status: KennismakingAutoStatus
  statusLabel: string
  /** Alleen tellingen, namen van middelen en datums; nooit gevoelige gegevens. */
  statusDetail: string | null
  /** Naar de plek waar het geregeld wordt (relatief, backoffice). */
  bronHref: string | null
  bronLabel: string | null
  details: KennismakingDetailDTO[]
  besproken: boolean
  besprokenOp: string | null
  nietVanToepassing: boolean
  nvtReden: string | null
  notitie: string | null
  actiepunten: KennismakingActiepuntDTO[]
  /** Wie een actiepunt op dit onderwerp standaard krijgt (null = kies zelf). */
  standaardHouder: { employeeId: string; naam: string } | null
}

export interface KennismakingVoortgangDTO {
  /** Geregeld van het aantal regels met een automatische status (zonder n.v.t.). */
  geregeld: number
  geregeldTotaal: number
  /** Besproken van alle regels (zonder n.v.t.). */
  besproken: number
  besprokenTotaal: number
  onbekend: number
  nietVanToepassing: number
}

/**
 * batch-71 integrator: `UIT` = de Outlook-uitnodiging staat (nog) uit bij Instellingen > Kennismaking
 * nieuwe medewerker. Een keuze, geen storing: geen melding en niet in het filter of de teller
 * "zonder agenda" (die zijn voor echte storingen).
 */
export type KennismakingAgendaStatus = 'VERSTUURD' | 'IN_WACHTRIJ' | 'NIET_AANGEMAAKT' | 'GEEN_GESPREK' | 'UIT'

export interface KennismakingGesprekDTO {
  id: string
  /** UTC-instant (ISO); de UI toont hem in Europe/Amsterdam. */
  geplandOp: string
  duurMinuten: number
  locatie: string | null
  status: 'GEPLAND' | 'VERZET' | 'AFGEROND' | 'GEANNULEERD'
  gesprekspartnerNaam: string | null
  verslagId: string | null
  verslagVrijgegeven: boolean
}

export interface KennismakingChecklistDTO {
  id: string
  status: KennismakingChecklistStatus
  statusLabel: string
  employeeId: string
  medewerkerNaam: string
  functie: string | null
  afdeling: string | null
  /** yyyy-mm-dd */
  startDatum: string
  contractId: string
  mijlpaalId: string
  mijlpaalStatus: string
  /** yyyy-mm-dd */
  deadlineOp: string | null
  dagenTotDeadline: number | null
  /** yyyy-mm-dd: de standaard-deadline van een actiepunt (START -1 werkdag). */
  standaardActiepuntDeadline: string
  verantwoordelijkeNaam: string | null
  geenVerantwoordelijke: boolean
  moetVerplaatsen: boolean
  korteAanloop: boolean
  gesprek: KennismakingGesprekDTO | null
  taakId: string | null
  agendaStatus: KennismakingAgendaStatus
  agendaProbleem: string | null
  regels: KennismakingRegelDTO[]
  voortgang: KennismakingVoortgangDTO
  actiepunten: KennismakingActiepuntDTO[]
  samenvatting: string | null
  afgerondOp: string | null
  afgerondDoorNaam: string | null
  annuleerReden: string | null
  magBewerken: boolean
  magHerplannen: boolean
  magAfronden: boolean
  /** Waarom afronden (nog) niet kan; null = kan. */
  afrondenUitleg: string | null
  /** De lijst in de backoffice, absoluut: de bronlinks staan daar (voor de portal). */
  backofficeUrl: string
}

/** Rij van het overzicht "Kennismakingen" (FO-534 §C). */
export interface KennismakingOverzichtRijDTO {
  checklistId: string
  employeeId: string
  medewerkerNaam: string
  functie: string | null
  /** yyyy-mm-dd */
  startDatum: string
  verantwoordelijkeNaam: string | null
  gesprekOp: string | null
  gesprekStatus: 'GEPLAND' | 'VERZET' | 'AFGEROND' | 'GEANNULEERD' | null
  status: KennismakingChecklistStatus
  statusLabel: string
  voortgang: KennismakingVoortgangDTO
  openActiepunten: number
  /** yyyy-mm-dd */
  deadlineOp: string | null
  teLaat: boolean
  agendaProbleem: string | null
}

export interface KennismakingOverzichtTellersDTO {
  open: number
  teLaat: number
  zonderAgenda: number
  dezeWeek: number
}

/** Dashboard-kaart bij de verantwoordelijke: "Kennismaking {naam} over 3 dagen". */
export interface KennismakingDashboardItemDTO {
  checklistId: string
  employeeId: string
  medewerkerNaam: string
  /** UTC-instant (ISO). */
  gesprekOp: string
  /** De taak van de kennismaking: de portal opent de lijst daaronder. */
  taakId: string | null
  /** Kalenderdagen tot het gesprek (0 = vandaag). */
  dagenTotGesprek: number
  /** Korte tekst: "Kennismaking Piet Jansen over 3 dagen". */
  titel: string
}

/** Portal: wat de medewerker van zijn kennismaking ziet (FO-534 §B9). */
export interface MijnKennismakingDTO {
  checklistId: string
  /** UTC-instant (ISO). */
  gesprekOp: string | null
  gesprekspartnerNaam: string | null
  samenvatting: string | null
  /** Labels van de besproken onderwerpen; nooit interne notities. */
  besprokenOnderwerpen: string[]
  eigenActiepunten: Array<{
    id: string
    omschrijving: string
    /** yyyy-mm-dd */
    deadline: string
    status: 'OPEN' | 'AFGEHANDELD'
    deeplink: string | null
    deeplinkLabel: string | null
  }>
  verslagId: string | null
}

/**
 * batch-71 integrator (delta-review): een gesprek dat al voorbij is, krijgt geen uitnodiging meer, ook
 * niet als de uitnodiging pas later is aangezet. Zichtbaar op de lijst, zonder melding.
 */
export const KENNISMAKING_GESPREK_VOORBIJ_REDEN =
  'Het gesprek is al voorbij: er gaat geen uitnodiging meer naar Outlook.'

export interface KennismakingInstellingDTO {
  onboardingContactUserIds: string[]
  onboardingContacten: Array<{ userId: string; employeeId: string | null; naam: string; actief: boolean }>
  priveEmailUitnodigen: boolean
  /** batch-71 integrator: de Outlook-uitnodiging staat uit tot de PO hem aanzet (B71-KM-OUTLOOK-AAN). */
  outlookUitnodigingActief: boolean
}

export interface KennismakingSjabloonRegelDTO extends KennismakingSjabloonRegelBasis {
  id: string
  volgorde: number
  actief: boolean
}

export interface KennismakingSjabloonDTO {
  id: string
  naam: string
  omschrijving: string | null
  isStandaard: boolean
  actief: boolean
  functie: string | null
  departmentId: string | null
  afdelingNaam: string | null
  regels: KennismakingSjabloonRegelDTO[]
  updatedAt: string
}

// ── Zod-schemas (request bodies + query) ─────────────────────────────────────

const ISO_DATUM = /^\d{4}-\d{2}-\d{2}$/
// Gesprekken worden op het kwartier gepland (zoals de gespreksagenda).
const KWARTIER_TIJD = /^([01]\d|2[0-3]):(00|15|30|45)$/

/** Eén onderwerp bijwerken: alleen wat is meegestuurd verandert. */
export const kennismakingRegelBijwerkenSchema = z
  .object({
    besproken: z.boolean().optional(),
    nietVanToepassing: z.boolean().optional(),
    nvtReden: z.string().trim().max(300, 'De reden is te lang (maximaal 300 tekens).').nullable().optional(),
    notitie: z.string().trim().max(4000, 'De notitie is te lang (maximaal 4000 tekens).').nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.nietVanToepassing === true && !val.nvtReden?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['nvtReden'],
        message: 'Geef een reden op waarom dit niet van toepassing is.',
      })
    }
  })
export type KennismakingRegelBijwerkenInput = z.infer<typeof kennismakingRegelBijwerkenSchema>

/**
 * Een open onderwerp wordt een actiepunt. Alles is optioneel: zonder invoer krijgt
 * het de standaard-houder en de standaard-deadline (één tik, FO-534 §B8).
 */
export const kennismakingActiepuntSchema = z.object({
  omschrijving: z.string().trim().min(1, 'Beschrijf het actiepunt.').max(500, 'Maximaal 500 tekens.').optional(),
  houderEmployeeId: z.string().min(1, 'Kies een actiehouder.').optional(),
  deadline: z.string().regex(ISO_DATUM, 'Kies een geldige datum.').optional(),
  gedeeldMetMedewerker: z.boolean().optional(),
})
export type KennismakingActiepuntInput = z.infer<typeof kennismakingActiepuntSchema>

export const kennismakingVerplaatsSchema = z.object({
  datum: z.string({ message: 'Kies een datum.' }).regex(ISO_DATUM, 'Kies een geldige datum.'),
  tijd: z
    .string({ message: 'Kies een tijd.' })
    .regex(KWARTIER_TIJD, 'Kies een tijd op het kwartier (bijvoorbeeld 10:15).'),
})
export type KennismakingVerplaatsInput = z.infer<typeof kennismakingVerplaatsSchema>

export const kennismakingAfzeggenSchema = z.object({
  reden: z
    .string({ message: 'Geef een reden op.' })
    .trim()
    .min(3, 'Geef een reden op.')
    .max(500, 'De reden is te lang (maximaal 500 tekens).'),
})
export type KennismakingAfzeggenInput = z.infer<typeof kennismakingAfzeggenSchema>

export const kennismakingAfrondenSchema = z.object({
  samenvatting: z
    .string({ message: 'Vul de samenvatting in.' })
    .trim()
    .min(1, 'Vul de samenvatting in: die ziet de medewerker in de portal.')
    .max(20000, 'De samenvatting is te lang.'),
})
export type KennismakingAfrondenInput = z.infer<typeof kennismakingAfrondenSchema>

export const kennismakingInstellingSchema = z.object({
  onboardingContactUserIds: z.array(z.string().min(1)).max(25, 'Kies maximaal 25 personen.'),
  priveEmailUitnodigen: z.boolean(),
  // batch-71 integrator (RUNBOOK-guardrail): geen echte uitnodiging voor het akkoord van de PO.
  outlookUitnodigingActief: z.boolean(),
})
export type KennismakingInstellingInput = z.infer<typeof kennismakingInstellingSchema>

const sjabloonRegelSchema = z.object({
  id: z.string().min(1).optional(),
  code: z
    .string()
    .trim()
    .regex(/^[A-Z0-9_]{1,40}$/, 'Gebruik hoofdletters, cijfers en een liggend streepje.'),
  groep: z.enum(KENNISMAKING_GROEPEN),
  label: z.string().trim().min(1, 'Geef het onderwerp een naam.').max(160),
  toelichting: z.string().trim().max(500).nullable(),
  checkSleutel: z.enum(KENNISMAKING_CHECK_SLEUTELS).nullable(),
  checkParams: z.record(z.string(), z.string()).nullable(),
  vrijeTekst: z.boolean(),
  voorMedewerker: z.boolean(),
  volgorde: z.number().int().min(0).max(1000),
  actief: z.boolean(),
})

export const kennismakingSjabloonSchema = z
  .object({
    naam: z.string().trim().min(1, 'Naam is verplicht.').max(120),
    omschrijving: z.string().trim().max(500).nullable().optional(),
    functie: z.string().trim().max(160).nullable().optional(),
    departmentId: z.string().min(1).nullable().optional(),
    actief: z.boolean(),
    regels: z.array(sjabloonRegelSchema).max(100, 'Maximaal 100 onderwerpen.'),
  })
  .superRefine((val, ctx) => {
    const codes = val.regels.map((r) => r.code)
    const dubbel = codes.filter((c, i) => codes.indexOf(c) !== i)
    if (dubbel.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['regels'],
        message: `Elke code mag een keer voorkomen (dubbel: ${[...new Set(dubbel)].join(', ')}).`,
      })
    }
    if (val.regels.filter((r) => r.actief).length === 0) {
      ctx.addIssue({ code: 'custom', path: ['regels'], message: 'Een lijst heeft minstens een onderwerp nodig.' })
    }
    val.regels.forEach((r, i) => {
      if (r.vrijeTekst && r.checkSleutel !== null) {
        ctx.addIssue({
          code: 'custom',
          path: ['regels', i, 'checkSleutel'],
          message: 'Een vrij tekstveld heeft geen automatische status.',
        })
      }
      if (r.label.includes('—') || (r.toelichting ?? '').includes('—')) {
        ctx.addIssue({
          code: 'custom',
          path: ['regels', i, 'label'],
          message: 'Gebruik een koppelteken met spaties in plaats van een lange streep.',
        })
      }
    })
  })
export type KennismakingSjabloonInput = z.infer<typeof kennismakingSjabloonSchema>

export const kennismakingenQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  status: z.enum(KENNISMAKING_CHECKLIST_STATUSSEN).optional(),
  verantwoordelijkeUserId: z.string().min(1).optional(),
  zoek: z.string().trim().max(100).optional(),
  /** Alleen te laat of zonder agenda-item. */
  aandacht: queryBoolean().optional(),
})
export type KennismakingenQuery = z.infer<typeof kennismakingenQuerySchema>

/** Labels voor de inline veldfouten (`summarizeValidationIssues`). */
export const KENNISMAKING_VELD_LABELS: Record<string, string> = {
  besproken: 'Besproken',
  nietVanToepassing: 'Niet van toepassing',
  nvtReden: 'Reden',
  notitie: 'Notitie',
  omschrijving: 'Omschrijving',
  houderEmployeeId: 'Actiehouder',
  deadline: 'Deadline',
  datum: 'Datum',
  tijd: 'Tijd',
  reden: 'Reden',
  samenvatting: 'Samenvatting',
  onboardingContactUserIds: 'Onboarding-contact',
  naam: 'Naam',
  regels: 'Onderwerpen',
  functie: 'Functie',
  departmentId: 'Afdeling',
}

/**
 * Waar de medewerker zijn eigen actiepunt afhandelt (portal, FO-534 §B9). Per groep
 * van het onderwerp; een groep zonder eigen plek heeft geen knop.
 */
export const KENNISMAKING_PORTAL_DEEPLINKS: Partial<Record<KennismakingGroep, { href: string; label: string }>> = {
  CERTIFICATEN: { href: '/mijn-gegevens', label: 'Certificaten uploaden' },
  // De keuzehulp voor ATV en TVT staat op Mijn gegevens (opbouw-keuzehulp).
  VERLOF_OPBOUW: { href: '/mijn-gegevens', label: 'Keuze ATV en TVT' },
  PROCEDURES: { href: '/documenten', label: 'Reglementen lezen' },
}

/**
 * De parameter van een controle, voor het sjabloonbeheer. Dezelfde waarden als de
 * parameter-schema's van de checks (`apps/api/src/lib/kennismaking/checks.ts`); de
 * registry-test houdt die twee gelijk.
 */
export const KENNISMAKING_CHECK_PARAM_VELDEN: Partial<
  Record<
    KennismakingCheckSleutel,
    { sleutel: string; label: string; opties?: ReadonlyArray<{ waarde: string; label: string }> }
  >
> = {
  'onboarding.middel': {
    sleutel: 'stap',
    label: 'Welk middel',
    opties: [
      { waarde: 'VOERTUIG', label: 'Auto' },
      { waarde: 'KLEDING', label: 'Kleding en PBM' },
      { waarde: 'TELEFOON_SIM', label: 'Telefoon en SIM' },
      { waarde: 'GEREEDSCHAP', label: 'Gereedschap' },
    ],
  },
  'onboarding.asset-reservering': {
    sleutel: 'type',
    label: 'Welke reservering',
    opties: [
      { waarde: 'LAPTOP', label: 'Laptop' },
      { waarde: 'TOEGANGSPAS', label: 'Toegangspas' },
      { waarde: 'OVERIG', label: 'Overig' },
    ],
  },
  'contract.opbouwkeuze-gezet': {
    sleutel: 'soort',
    label: 'Welke keuze',
    opties: [
      { waarde: 'ATV', label: 'ATV' },
      { waarde: 'TVT', label: 'TVT' },
    ],
  },
  'document.leesbevestiging': { sleutel: 'titelBevat', label: 'Alleen reglementen met in de titel' },
}
